'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { TemplateCard } from '@/app/components/shared/TemplateCard';
import { TemplateDetailDrawer } from '@/app/components/shared/TemplateDetailDrawer';
import { useExpose, useFetch, useIsScreen, useTemplateDetail } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { TemplateData } from './[scene]/types';
import { templatesList } from './apis';
import { useTemplatesPanel } from './PanelContext';

const PAGE_SIZE = 24;

interface TemplatesSelectPanelInnerProps {
  currentTemplateId?: string;
}

const TemplatesSelectPanelInner: FC<TemplatesSelectPanelInnerProps> = ({ currentTemplateId }) => {
  const { isSmScreen } = useIsScreen();
  const t = useTranslations('templates');
  const router = useRouter();
  const { updateTemplateFormParams } = useStore((state) => ({
    updateTemplateFormParams: state.updateTemplateFormParams,
  }));
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const pageTokenRef = useRef<string>('');
  const scrollId = useId();
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const { isCollapsed, setCollapsed, setTemplateDetailDrawerOpen } = useTemplatesPanel();
  const templateCardRef = useRef<HTMLDivElement[]>([]);
  const { detail, openDetail, closeDetail, onPrev, onNext, hasPrev, hasNext } = useTemplateDetail({
    templates,
    hasMore,
    isLoading: false,
    loadMore: () => getTemplates(),
    reportPosition: 'templates_tool',
  });

  useEffect(() => {
    // 小屏不渲染侧栏与 TemplateDetailDrawer，避免 detail 残留导致误禁用全局拖拽
    setTemplateDetailDrawerOpen(Boolean(detail?.scene) && !isSmScreen);
  }, [detail?.scene, isSmScreen, setTemplateDetailDrawerOpen]);

  const needsScrollRef = useRef<boolean>(false);

  useExpose(
    (idx) => {
      const item = templates[idx];
      if (item) {
        reportEvent('templates_element_exposure', {
          element_name: `template_${item.scene}` || '',
          position: 'templates_tool',
        });
      }
    },
    templateCardRef.current,
    templates,
  );

  const { run: getTemplates } = useFetch(
    () =>
      templatesList({
        query: {
          'pager.page_token': pageTokenRef.current,
          'pager.pagesz': PAGE_SIZE,
          use_rec: true,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) {
          setTemplates((prev) => [...prev, ...data.templates]);
          pageTokenRef.current = data.next_page_token || '';
          setHasMore(Boolean(data.next_page_token));
        }
      },
    },
  );

  useEffect(() => {
    setTemplates([]);
    pageTokenRef.current = '';
    getTemplates();
  }, []);

  useEffect(() => {
    if (!currentTemplateId) return;
    const exists = templates.some((t) => t.id === currentTemplateId);
    if (!exists && hasMore) {
      getTemplates();
    }

    // 加载完当前模版数据后，如果之前标记了需要滚动到当前选中模板，则滚动到当前选中模板
    if (exists && needsScrollRef.current) {
      scrollToCurrentTemplate();
    }
  }, [currentTemplateId, templates, hasMore]);

  // 展开模板选择面板时，滚动到当前选中模板
  useEffect(() => {
    if (isCollapsed) {
      needsScrollRef.current = false;
      return;
    }

    needsScrollRef.current = true;
    scrollToCurrentTemplate();
  }, [isCollapsed]);

  const scrollToCurrentTemplate = () => {
    const container = listContainerRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLDivElement>(`[data-selected="true"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      needsScrollRef.current = false;
    }
  };

  const handleSwitchTemplate = (scene: string) => {
    if (scene === currentTemplateId) return;
    // 清空 settings
    updateTemplateFormParams('settings', {});
    // 切换模板
    router.push(`/templates/${scene}`);
  };

  if (isSmScreen) return null;

  return (
    <div
      className={cn(
        // Desktop / large screens: reserve width only when expanded
        'relative h-full shrink-0 transition-[width] duration-200',
        isCollapsed ? 'w-0' : 'w-[300px]',
        // Small screens: fixed overlay container
        'sm:fixed sm:inset-0 sm:z-50 sm:m-0 sm:h-full sm:w-full sm:transition-opacity',
        isCollapsed ? 'sm:pointer-events-none sm:opacity-0' : 'sm:pointer-events-auto sm:opacity-100',
        !isCollapsed && 'sm:bg-system-black32/40 sm:backdrop-blur-[2px]',
      )}
      role={isSmScreen ? 'dialog' : undefined}
      aria-modal={isSmScreen ? true : undefined}
      aria-label={t('title')}
    >
      {/* Toggle button */}
      <button
        type="button"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        onClick={() => setCollapsed(!isCollapsed)}
        className={cn(
          'absolute top-1/2 right-0 z-20 flex h-[72px] w-6 items-center justify-center rounded-full border border-system-white08 bg-system-white04 shadow backdrop-blur-sm hover:bg-system-white08',
          isCollapsed && 'rounded-r-12 rounded-l-none',
        )}
        style={{ transform: 'translate(50%, -50%)' }}
      >
        <BaseLineLeft className={cn('text-base', isCollapsed && 'rotate-180')} />
      </button>

      <div
        className={cn(
          // Desktop sidebar panel
          'absolute inset-y-0 left-0 w-[300px] overflow-hidden border-system-white08 border-r bg-system-bg08/95 backdrop-blur-md transition-transform duration-200',
          isCollapsed ? 'hidden sm:block' : 'block',
          // Small screens: slide-in panel from left
          'sm:absolute sm:top-0 sm:left-0 sm:h-full sm:w-[300px] sm:max-w-[300px] sm:border-r sm:border-l-0 sm:shadow-xl sm:transition-transform sm:duration-200',
          isCollapsed && 'sm:-translate-x-full',
        )}
      >
        <div className="sticky top-0 z-10 flex h-20 items-center px-6 md:h-16">
          <h3 className="font-medium text-sm">{t('title')}</h3>
        </div>

        <div id={scrollId} ref={listContainerRef} className="hide-scrollbar h-full overflow-y-auto px-6">
          <InfiniteScroll
            dataLength={templates.length}
            next={() => getTemplates()}
            hasMore={hasMore}
            loader={<div className="py-4 text-center text-sm text-system-white48">Loading...</div>}
            scrollableTarget={scrollId}
          >
            <div className="grid grid-cols-2 gap-x-2 gap-y-6 px-0.5 pt-0.5 text-center">
              {templates.map((item, idx) => (
                <div
                  ref={(el: HTMLDivElement) => {
                    templateCardRef.current[idx] = el;
                  }}
                  key={item.id}
                  data-index={idx}
                  data-selected={item.id === currentTemplateId}
                >
                  <TemplateCard
                    variant="portrait"
                    resource={item.creations?.find((creation) => creation.name === 'portrait')?.resource}
                    title={item.scene_name || ''}
                    titleClassName="ml-0 text-xs"
                    className={cn(
                      'border-2 border-transparent',
                      item.id === currentTemplateId && 'ring-2 ring-system-white ring-offset-transparent',
                    )}
                    tag={item.tag}
                    showCreate
                    size="small"
                    onClick={() => {
                      handleSwitchTemplate(item.scene);
                      reportEvent('templates_element_click', {
                        element_name: `template_${item.scene}`,
                        position: 'templates_tool',
                      });
                    }}
                    onViewDetail={() => {
                      openDetail(item.scene, item.scene_name || '', idx);
                    }}
                    isImageType={item.type === 'image'}
                  />
                </div>
              ))}
            </div>
          </InfiniteScroll>
        </div>
      </div>
      {/* 始终渲染 TemplateDetailDrawer，避免路由拦截时卸载导致数据丢失 */}
      <TemplateDetailDrawer
        open={!!detail?.scene}
        scene={detail?.scene || ''}
        title={detail?.title || ''}
        onClose={closeDetail}
        onPrev={onPrev}
        onNext={onNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onClickCreate={() => {
          handleSwitchTemplate(detail?.scene || '');
          reportEvent('templates_element_click', {
            element_name: `detail_create_${detail?.scene}`,
            position: 'templates_tool',
          });
        }}
      />
    </div>
  );
};

export const TemplatesSelectPanel: FC = () => {
  const [sidePanelContainer, setSidePanelContainer] = useState<HTMLElement | null>(null);
  const formParams = useStore((state) => state.sceneFormParams);

  useEffect(() => {
    const container = document.getElementById('templates-sidebar-container');
    setSidePanelContainer(container);
  }, []);

  if (!sidePanelContainer) return null;

  return createPortal(<TemplatesSelectPanelInner currentTemplateId={formParams.templateId} />, sidePanelContainer);
};
